import { useState } from "react";
import { MessageSquarePlus, Pencil, Trash2, User, Users } from "lucide-react";
import { NoteForm } from "./NoteForm";
import { useNotes } from "../lib/notesContext";
import { useAuth } from "../lib/auth";
import { catLabel, periodLabel, overlaps } from "../lib/notes";

export const NoteItem = ({ n, pickers, compact, missing }) => {
  const { remove } = useNotes();
  const { user, isManager } = useAuth();
  const [edit, setEdit] = useState(false);
  const canEdit = isManager || n.author === user?.email;
  return (
    <div className="rounded-lg border border-slate-800 bg-[#0B0F19] p-3" data-testid={`note-${n.id}`}>
      <div className="flex flex-wrap items-center gap-2 text-[11px]">
        <span className="font-mono text-slate-200">{periodLabel(n)}</span>
        <span className="px-1.5 py-0.5 rounded border border-ep/40 text-ep-soft">{catLabel(n.category)}</span>
        <span className="inline-flex items-center gap-1 text-slate-400">{n.scope === "team" ? <><Users className="h-3 w-3" /> Team</> : <><User className="h-3 w-3" /> {n.picker}</>}</span>
        {missing && <span className="text-amber-300" title="This picker isn't in the current data">not in current data</span>}
        {canEdit && !compact && <span className="ml-auto flex gap-1 no-print">
          <button data-testid={`note-edit-${n.id}`} onClick={() => setEdit(true)} className="p-1 rounded hover:bg-slate-800 text-slate-400"><Pencil className="h-3.5 w-3.5" /></button>
          <button data-testid={`note-delete-${n.id}`} onClick={() => window.confirm("Delete this note?") && remove(n.id)} className="p-1 rounded hover:bg-slate-800 text-slate-400"><Trash2 className="h-3.5 w-3.5" /></button>
        </span>}
      </div>
      <p className="text-sm text-slate-200 mt-2 whitespace-pre-wrap">{n.text}</p>
      <p className="text-[10px] text-slate-500 mt-2">{n.author} · {new Date(n.updatedAt).toLocaleString("en-GB")}{n.updatedAt !== n.createdAt ? " (edited)" : ""}</p>
      {edit && <NoteForm open={edit} onOpenChange={setEdit} note={n} pickers={pickers} />}
    </div>
  );
};

export const NotesPanel = ({ title = "Commentary", start, end, picker, includeTeam = true, defaults, pickers = [], testId = "notes-panel", empty = "No commentary for this period yet.", limit }) => {
  const { notes } = useNotes();
  const [adding, setAdding] = useState(false);
  const list = notes.filter((n) => (!start || overlaps(n, start, end)) && (!picker ? true : n.picker === picker || (includeTeam && n.scope === "team")));
  const shown = limit ? list.slice(0, limit) : list;
  return (
    <div data-testid={testId}>
      <div className="flex items-center gap-2 mb-2">
        <p className="label-mono mr-auto">{title} {list.length > 0 && <span className="text-slate-400">· {list.length}</span>}</p>
        <button data-testid={`${testId}-add`} onClick={() => setAdding(true)} className="no-print inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border border-slate-700 text-slate-200 hover:bg-slate-800"><MessageSquarePlus className="h-3.5 w-3.5" /> Add note</button>
      </div>
      <div className="space-y-2">
        {shown.length === 0 && <p className="text-xs text-slate-500">{empty}</p>}
        {shown.map((n) => <NoteItem key={n.id} n={n} pickers={pickers} />)}
        {limit && list.length > limit && <p className="text-[11px] text-slate-500">+{list.length - limit} more on the Notes page</p>}
      </div>
      {adding && <NoteForm open={adding} onOpenChange={setAdding} defaults={defaults} pickers={pickers} />}
    </div>
  );
};
